// Queries de catálogos (granjas-origen, tipos de pienso, curva esperada).
//
// Decisión 5 del ADR-022: staleTime 24h. Los catálogos casi no cambian;
// un pull-to-refresh explícito en la página que las muestra refresca.
//
// v0.5.1 (ADR-029): se elimina ``useEstirpes``. La curva esperada pasa a
// ser por sexo (``useCurvaEsperada``) — antes vivía bajo estirpe.

import { apiFetch, qk, useApiMutation, useApiQuery } from './client';
import type {
  CurvaEstandar,
  CurvaPienso,
  PuntoCurva,
  PuntoCurvaPienso,
  TipoPienso,
} from '@/lib/types';

const CATALOGOS_KEY = qk('catalogos');

export function useTiposPienso() {
  return useApiQuery<TipoPienso[]>({
    queryKey: [...CATALOGOS_KEY, 'tipos-pienso'],
    queryFn: () => apiFetch<TipoPienso[]>('/v1/catalogos/tipos-pienso'),
    staleTime: 24 * 60 * 60 * 1000,
  });
}

/** Curva esperada de la explotación por sexo (Ajustes). */
export function useCurvaEsperada(sexo: 'machos' | 'hembras') {
  return useApiQuery<CurvaEstandar>({
    queryKey: [...CATALOGOS_KEY, 'curva-esperada', sexo],
    queryFn: () => apiFetch<CurvaEstandar>(`/v1/catalogos/curva-esperada?sexo=${sexo}`),
    staleTime: 30 * 1000,
  });
}

/** Guarda (reemplaza) la curva esperada de la explotación por sexo. */
export function useGuardarCurvaEsperada(sexo: 'machos' | 'hembras') {
  return useApiMutation<{ puntos: PuntoCurva[] }, CurvaEstandar>({
    mutationFn: (body, context) =>
      apiFetch<CurvaEstandar>(`/v1/catalogos/curva-esperada?sexo=${sexo}`, {
        offlineBase: context.offlineBase,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [['liquidacion-evolucion']],
  });
}

/** Curva de pienso acumulado configurada en Ajustes por sexo. */
export function useCurvaPiensoAjustes(sexo: 'machos' | 'hembras') {
  return useApiQuery<CurvaPienso>({
    queryKey: [...CATALOGOS_KEY, 'curva-pienso', sexo],
    queryFn: () => apiFetch<CurvaPienso>(`/v1/catalogos/curva-pienso?sexo=${sexo}`),
    staleTime: 30 * 1000,
  });
}

/** Guarda la curva de pienso acumulado configurada en Ajustes. */
export function useGuardarCurvaPienso(sexo: 'machos' | 'hembras') {
  return useApiMutation<{ puntos: PuntoCurvaPienso[] }, CurvaPienso>({
    mutationFn: (body, context) =>
      apiFetch<CurvaPienso>(`/v1/catalogos/curva-pienso?sexo=${sexo}`, {
        offlineBase: context.offlineBase,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [[...CATALOGOS_KEY, 'curva-pienso', sexo], qk('entregas-pienso', 'curva')],
  });
}
